import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Form, Row, Col, Card, Spinner } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import { BsArrowClockwise, BsFunnelFill, BsSearch } from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ExpandableReasonText from './commonComponents/ExpandableReasonText'

const filterControlStyle = {
  borderRadius: '8px',
  border: '1px solid #D1D3D4',
  fontSize: '14px',
  height: '40px',
  backgroundColor: '#ffffff',
  fontFamily: 'Inter, system-ui, sans-serif',
}

const formatApiDate = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null
  }
  return date.toISOString().split('T')[0]
}

export default function MyTeacherAttendance() {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo } = useInstitute()
  const [showFilterCard, setShowFilterCard] = useState(true)
  const [openTable, setOpenTable] = useState(false)
  const [attendanceData, setAttendanceData] = useState([])
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all')
  const [loadingAttendance, setLoadingAttendance] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  // Date filters go to the API; status is applied client-side (API may ignore status).
  const buildQueryParams = useCallback(() => {
    const params = {}
    const start = formatApiDate(fromDate)
    const end = formatApiDate(toDate)

    if (start && end) {
      params.start_date = start
      params.end_date = end
    } else if (start) {
      params.date = start
    } else if (end) {
      params.date = end
    }

    return params
  }, [fromDate, toDate])

  const fetchAttendance = useCallback(async () => {
    setLoadingAttendance(true)
    try {
      const res = await ApiEndpoints.getMyTeacherAttendance(buildQueryParams())
      setAttendanceData(res.data?.attendance || [])
      setOpenTable(true)
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          'Failed to load your attendance.',
      )
      setAttendanceData([])
      setOpenTable(true)
    } finally {
      setLoadingAttendance(false)
    }
  }, [buildQueryParams])

  useEffect(() => {
    fetchAttendance()
  }, [fetchAttendance])

  const filteredAttendanceData = useMemo(() => {
    if (attendanceStatusFilter === 'all') {
      return attendanceData
    }

    return attendanceData.filter(
      (row) =>
        String(row.status || '')
          .toLowerCase()
          .replace(/\s+/g, '_') === attendanceStatusFilter,
    )
  }, [attendanceData, attendanceStatusFilter])

  const handleRefresh = () => {
    setFromDate(null)
    setToDate(null)
    setAttendanceStatusFilter('all')
    // Always refetch with cleared date params (avoid stale closure after reset).
    setLoadingAttendance(true)
    ApiEndpoints.getMyTeacherAttendance({})
      .then((res) => {
        setAttendanceData(res.data?.attendance || [])
        setOpenTable(true)
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.message ||
            'Failed to load your attendance.',
        )
        setAttendanceData([])
        setOpenTable(true)
      })
      .finally(() => {
        setLoadingAttendance(false)
      })
  }

  const getStatusColor = (status) => {
    const s = String(status || '').toLowerCase()
    if (s === 'present') return '#10B981'
    if (s === 'absent') return '#EF4444'
    if (s === 'leave') return '#F59E0B'
    if (s === 'half_day') return '#3B82F6'
    if (s === 'late') return '#8B5CF6'
    return '#111827'
  }

  const getRowBgColor = (status, index) => {
    const s = String(status || '').toLowerCase()
    if (s === 'present') return 'rgba(16, 185, 129, 0.1)'
    if (s === 'absent') return 'rgba(239, 68, 68, 0.1)'
    return index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
  }

  const formatDisplayDate = (dateValue) => {
    if (!dateValue) return 'N/A'
    const parsed = new Date(dateValue)
    if (Number.isNaN(parsed.getTime())) return 'N/A'
    return parsed.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }

  const formatDisplayStatus = (status) => {
    if (!status) return 'N/A'
    return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')
  }

  return (
    <>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                padding: '12px 28px',
                borderBottom: '1px solid #D1D3D4',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#fff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    color: '#223843',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  My Attendance
                </h3>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            padding: isCollapsed ? '32px' : '40px',
          }}
        >
          <div className="d-flex align-items-center mb-4">
            <button
              type="button"
              onClick={() => setShowFilterCard((open) => !open)}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                border: '1px solid #D1D3D4',
                color: showFilterCard ? '#ffffff' : '#223843',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
            >
              <BsFunnelFill size={20} />
            </button>
          </div>

          {showFilterCard && (
          <Card
            className="mb-4"
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              backgroundColor: '#ffffff',
            }}
          >
            <div
              style={{
                background: '#ffffff',
                padding: '20px',
                borderBottom: '1px solid #F3F4F6',
              }}
            >
              <div className="d-flex justify-content-between align-items-center gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#223843',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                  </div>
                  <div className="text-start">
                    <h5
                      className="m-0 fw-semibold"
                      style={{
                        color: '#111827',
                        fontSize: '16px',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Filter Attendance
                    </h5>
                    <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                      Choose date range and status to view attendance
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleRefresh}
                  disabled={loadingAttendance}
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#10B981',
                    border: 'none',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: loadingAttendance ? 'not-allowed' : 'pointer',
                    opacity: loadingAttendance ? 0.6 : 1,
                    flexShrink: 0,
                  }}
                  title="Refresh"
                >
                  <BsArrowClockwise size={20} />
                </button>
              </div>
            </div>

            <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
              <Row className="g-3 text-start flex-lg-nowrap">
                <Col xs={12} lg={4}>
                  <label
                    className="fw-semibold mb-2 d-block"
                    style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Start Date
                  </label>
                  <Form.Control
                    type="date"
                    value={
                      fromDate instanceof Date && !Number.isNaN(fromDate)
                        ? fromDate.toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFromDate(e.target.value ? new Date(e.target.value) : null)
                    }
                    disabled={loadingAttendance}
                    style={filterControlStyle}
                  />
                </Col>
                <Col xs={12} lg={4}>
                  <label
                    className="fw-semibold mb-2 d-block"
                    style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    End Date
                  </label>
                  <Form.Control
                    type="date"
                    value={
                      toDate instanceof Date && !Number.isNaN(toDate)
                        ? toDate.toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setToDate(e.target.value ? new Date(e.target.value) : null)
                    }
                    disabled={loadingAttendance}
                    style={filterControlStyle}
                  />
                </Col>
                <Col xs={12} lg={4}>
                  <label
                    className="fw-semibold mb-2 d-block"
                    style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Attendance Status
                  </label>
                  <Form.Select
                    value={attendanceStatusFilter}
                    onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                    disabled={loadingAttendance}
                    style={filterControlStyle}
                  >
                    <option value="all">All</option>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="leave">Leave</option>
                    <option value="half_day">Half Day</option>
                    <option value="late">Late</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          )}

          {loadingAttendance && (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#223843' }} />
            </div>
          )}

          {!loadingAttendance && openTable && (
            <Card style={{ border: '1px solid #D1D3D4', borderRadius: 12 }}>
              <Table className="table mb-0 text-start">
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    <Th>Date</Th>
                    <Th>Status</Th>
                    <Th>Remarks</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredAttendanceData.length > 0 ? (
                    filteredAttendanceData.map((att, index) => (
                      <Tr
                        key={att.id || `${att.date}-${index}`}
                        style={{ backgroundColor: getRowBgColor(att.status, index) }}
                      >
                        <Td>{formatDisplayDate(att.date)}</Td>
                        <Td style={{ color: getStatusColor(att.status), fontWeight: 600 }}>
                          {formatDisplayStatus(att.status)}
                        </Td>
                        <Td
                          style={{
                            minWidth: isMobile ? 'auto' : '180px',
                            maxWidth: isMobile ? '100%' : '340px',
                            verticalAlign: 'top',
                          }}
                        >
                          <ExpandableReasonText
                            text={att.remarks || '—'}
                            isMobile={isMobile}
                            charLimit={30}
                            contentLabel="Remarks"
                            modalTitle="Attendance Details"
                            modalDetails={[
                              { label: 'Date', value: formatDisplayDate(att.date) },
                              {
                                label: 'Status',
                                value: formatDisplayStatus(att.status),
                              },
                            ]}
                          />
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan="3" className="text-center py-4 text-muted">
                        No attendance records found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
